/**
    * @description      : 
    * @author           : 
    * @group            : 
    * @created          : 15/09/2025 - 17:00:24
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 15/09/2025
    * - Author          : 
    * - Modification    : 
**/

import React, { useEffect, useState } from "react";
import { Image, Button, Spinner } from "react-bootstrap";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import images from "../../images/index";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Navbar from "../../components/shared/navbar";
import Footer from "../../components/shared/footer";
import { doc, getDoc } from "firebase/firestore";
import { db, auth } from "../../firebase";
import { downloadApi } from "../../api/secureurlapi";
import { toast } from "react-toastify";
import { useTranslation } from 'react-i18next';

interface ReportCardProps {
  date: string;
  fileUrl: {
    glareFound: boolean;
    excelUrl: string;
    paidReportUrl: string;
  };
  owner: string;
  paymentStatus: string;
  projectName: string;
  tiltedPVArea: number;
  verticalPVArea: number;
  detectionPoints: string;
  id: string;
  projectId: string;
  imageUrl: string;
}

interface DownloadState {
  paid: boolean;
  excel: boolean;
}

const PaidProject: React.FC = () => {
  const { t } = useTranslation(['paidProject', 'common']);
  const [data, setData] = useState<ReportCardProps[]>([]);
  const [secureUrls, setSecureUrls] = useState({
    paid: null as string | null,
    excel: null as string | null
  });
  const [isLoading, setIsLoading] = useState({
    document: true,
    paid: false,
    excel: false
  });
  const navigate = useNavigate();
  const location = useLocation();
  const docId = location.state;

  // Fetch report data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(prev => ({ ...prev, document: true }));
        
        if (!docId) {
          toast.error("Report ID not provided");
          navigate("/myreports");
          return;
        }
        
        const docRef = doc(db, "paidReport", docId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setData([{ ...docSnap.data(), id: docSnap.id } as ReportCardProps]);
          
          // Check access
          const accessInfo = await downloadApi.checkReportAccess(docId);
          
          if (!accessInfo?.success || !accessInfo?.data?.hasAccess) {
            toast.error("You don't have access to this report");
            navigate("/myreports");
            return;
          }
          
          // If not paid, redirect to unpaid view
          if (accessInfo.data && !accessInfo.data.paidAccess) {
            toast.info("Please complete payment to access the full report");
            navigate(`/projectOverview/${docSnap.data().projectId}`);
            return;
          }
          
          // Load the paid report URL
          const paidUrl = await downloadApi.getSecureDownloadUrl(docId, 'paid');
          setSecureUrls(prev => ({ ...prev, paid: paidUrl }));
        } else {
          toast.error("Report not found");
          navigate("/myreports");
        }
      } catch (error) {
        console.error("Error getting document:", error);
        toast.error("Error loading report");
      } finally {
        setIsLoading(prev => ({ ...prev, document: false }));
      }
    };

    fetchData();
  }, [docId, navigate]);

  // Set up a refresh interval (URLs expire after 15 minutes)
  useEffect(() => {
    if (!docId || !data.length) return;
    
    const refreshInterval = setInterval(async () => {
      // Only refresh if the component is still mounted and visible
      if (document.visibilityState === 'visible') {
        // Only refresh what's currently loaded
        if (secureUrls.paid) {
          const paidUrl = await downloadApi.getSecureDownloadUrl(docId, 'paid');
          if (paidUrl) setSecureUrls(prev => ({ ...prev, paid: paidUrl }));
        }
        
        if (secureUrls.excel) {
          const excelUrl = await downloadApi.getSecureDownloadUrl(docId, 'excel');
          if (excelUrl) setSecureUrls(prev => ({ ...prev, excel: excelUrl }));
        }
      }
    }, 10 * 60 * 1000); // Refresh every 10 minutes
    
    return () => clearInterval(refreshInterval);
  }, [docId, data, secureUrls.paid, secureUrls.excel]);

  // Handle PDF download
  const handlePdfDownload = async () => {
    if (isLoading.paid) return;
    
    setIsLoading(prev => ({ ...prev, paid: true }));
    try {
      const url = await downloadApi.getSecureDownloadUrl(docId, 'paid');
      
      if (url) {
        // Open in a new tab
        window.open(url, '_blank');
        setSecureUrls(prev => ({ ...prev, paid: url }));
      } else {
        toast.error("Failed to generate download link");
      }
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Download failed. Please try again.");
    } finally {
      setIsLoading(prev => ({ ...prev, paid: false }));
    }
  };

  // Handle Excel download
  const handleExcelDownload = async () => {
    if (isLoading.excel) return;
    
    setIsLoading(prev => ({ ...prev, excel: true }));
    try {
      const url = await downloadApi.getSecureDownloadUrl(docId, 'excel');
      
      if (url) {
        // Open in a new tab
        window.open(url, '_blank');
        setSecureUrls(prev => ({ ...prev, excel: url }));
      } else {
        toast.error("Failed to generate download link");
      }
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Download failed. Please try again.");
    } finally {
      setIsLoading(prev => ({ ...prev, excel: false }));
    }
  };

  return (
    <React.Fragment>
      <Navbar />
      {isLoading.document ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
          <div className="text-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-3">{t('common:loading')}</p>
          </div>
        </div>
      ) : data.length > 0 ? (
        <div>
          <div className="mt-4 d-flex align-items-end">
            <Link
              style={{ width: "fit-content" }}
              className="text-decoration-none"
              to="/myreports"
            >
              <div className="mx-3 d-flex align-items-center">
                <Image className="mb-3" src={images.leftArrow} alt="back_arrow" />
                <p className="d-none d-sm-inline mx-2 backToDashboard">
                  {t('paidProject:paidProject.navigation.backToReports')}
                </p>
              </div>
            </Link>
          </div>

          <Container fluid className="mt-4 mb-5 pt-3">
            <Row className="justify-content-center gap-3">
              <Col className="d-flex justify-content-center">
                <div style={{ width: "500px" }}>
                  <div className="d-sm-flex justify-content-between align-items-center PO-MYHOME">
                    <p className="mt-3 mb-0 unpaidtextOverFlow">
                      {data[0]?.projectName}
                    </p>
                    <p className="mt-3 mb-0">{t('paidProject:paidProject.projectDetails.id')}: {data[0]?.id}</p>
                  </div>
                  <hr />
                  <div>
                    <div className="d-flex justify-content-between">
                      <p className="PO-date">{t('paidProject:paidProject.projectDetails.date')}:</p>
                      <p className="POdata">{`${data[0]?.date}`}</p>
                    </div>
                    <div className="d-flex justify-content-between">
                      <p className="PO-date">{t('paidProject:paidProject.projectDetails.pvAreas')}:</p>
                      <p className="POdata">
                        <span className="m-0 p-0">
                          {`${data[0]?.verticalPVArea} ${t('paidProject:paidProject.projectDetails.vertical')}, ${data[0]?.tiltedPVArea} ${t('paidProject:paidProject.projectDetails.tilted')}`}
                        </span>
                      </p>
                    </div>
                    <div className="d-flex justify-content-between">
                      <p className="PO-date">{t('paidProject:paidProject.projectDetails.detectionPoints')}:</p>
                      <p className="POdata">
                        {data[0]?.detectionPoints} {t('paidProject:paidProject.projectDetails.detectionPointsLabel')}
                      </p>
                    </div>
                    
                    {/* PDF Download Button */}
                    <Button 
                      className="paid-downloadPDF w-100 mb-2" 
                      variant="none"
                      disabled={isLoading.paid}
                      onClick={handlePdfDownload}
                    >
                      {isLoading.paid ? (
                        <>
                          <Spinner 
                            as="span" 
                            animation="border" 
                            size="sm" 
                            role="status" 
                            aria-hidden="true" 
                            className="me-2"
                          />
                          {t('paidProject:common.loading')}
                        </>
                      ) : (
                        t('paidProject:paidProject.buttons.downloadPdf')
                      )}
                    </Button>
                    
                    {/* Excel Download Button */}
                    <Button 
                      className="paid-downloadPDF w-100" 
                      variant="none"
                      disabled={isLoading.excel}
                      onClick={handleExcelDownload}
                    >
                      {isLoading.excel ? (
                        <>
                          <Spinner 
                            as="span" 
                            animation="border" 
                            size="sm" 
                            role="status" 
                            aria-hidden="true" 
                            className="me-2"
                          />
                          {t('paidProject:common.loading')}
                        </>
                      ) : (
                        t('paidProject:paidProject.buttons.downloadExcel')
                      )}
                    </Button>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
          
          <div
            className="d-flex justify-content-center"
            style={{ marginBottom: "100px" }}
          >
            {!secureUrls.paid ? (
              <div className="text-center p-5">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p className="mt-3">{t('paidProject:loading.report')}</p>
              </div>
            ) : data[0]?.fileUrl?.glareFound === false ? (
              <div>
                <h1 className="glareNotFound mt-5">{t('paidProject:glareNotFound')}</h1>
              </div>
            ) : (
              <iframe
                src={secureUrls.paid}
                style={{
                  width: `700px`,
                  height: "800px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                }}
                title="Full Report Preview"
              />
            )}
          </div>
        </div>
      ) : (
        <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
          <div className="text-center">
            <p>{t('paidProject:errors.reportNotFound')}</p>
            <Link to="/myreports" className="btn btn-primary mt-3">
              {t('paidProject:navigation.backToReports')}
            </Link>
          </div>
        </div>
      )}
      <Footer />
    </React.Fragment>
  );
};

export default PaidProject;